/**
 * Session 30.40 — Reports → Spreadsheets.
 *
 * An Excel-style workspace inside the POS so owners/managers don't need
 * to bounce out to Excel/Google Sheets. Built on FortuneSheet (formulas,
 * cell formatting, multiple sheet tabs, freeze/merge/sort…) with:
 *   • saved documents + full version history (open a sheet as it was on
 *     any past save; restore never rewrites history)
 *   • search + date filtering of the document list
 *   • import .xlsx / .csv, export .xlsx / .csv, print
 *   • restaurant starter templates (cash-up, stock count, roster)
 *   • "Send report to spreadsheet" entry point (see buildSheetFromRows)
 *
 * Storage format = FortuneSheet sheet objects (name + sparse `celldata`).
 */
import * as React from "react";
import * as XLSX from "xlsx";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import {
  ArrowLeft, Copy, Download, FileSpreadsheet, FileUp, History, Plus, Printer,
  RefreshCw, Save, Search, Trash2, X, Calendar, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { apiErrorMessage } from "@/lib/apiError";

// ───────────────────────────────────────────────────────────────────────────
// Cell helpers (FortuneSheet <-> plain values <-> SheetJS)
// ───────────────────────────────────────────────────────────────────────────

const numCell = (v, opts = {}) => ({ v, m: String(v), ct: { fa: opts.fa || "General", t: "n" }, ...(opts.bl ? { bl: 1 } : {}) });
const txtCell = (v, opts = {}) => ({ v: String(v ?? ""), m: String(v ?? ""), ct: { fa: "General", t: "g" }, ...(opts.bl ? { bl: 1 } : {}), ...(opts.bg ? { bg: opts.bg } : {}) });
// Formula cells carry a cached value (`v`/`m`) — FortuneSheet renders the
// cache and recalculates on the next edit, so seed it (0 for templates,
// the computed total for report exports).
const fmlCell = (f, opts = {}) => {
  const v = opts.v === undefined || opts.v === null ? 0 : opts.v;
  return { f: f.startsWith("=") ? f : `=${f}`, v, m: String(v), ct: { fa: opts.fa || "General", t: "n" }, ...(opts.bl ? { bl: 1 } : {}) };
};

const colLetter = (c) => { let s = ""; c += 1; while (c > 0) { const m = (c - 1) % 26; s = String.fromCharCode(65 + m) + s; c = Math.floor((c - 1) / 26); } return s; };

/** Turn a 2-D array of raw values (strings/numbers/null, or a `{f:"=..."}`
 *  formula object) into a FortuneSheet sheet. Row 0 is styled as a header. */
/** FortuneSheet only re-evaluates formulas it knows about: register every
 *  formula cell in the sheet's `calcChain` so edits to referenced cells
 *  recalculate live (like Excel). */
function withCalcChain(sheet) {
  const id = sheet.id || `sheet_${Math.random().toString(36).slice(2, 10)}`;
  const calcChain = (sheet.celldata || [])
    .filter((cd) => cd?.v && typeof cd.v.f === "string" && cd.v.f.length > 1)
    .map((cd) => ({ r: cd.r, c: cd.c, id }));
  return { ...sheet, id, calcChain };
}

export function buildSheetFromRows(name, rows, { headerRow = true } = {}) {
  const celldata = [];
  (rows || []).forEach((row, r) => {
    (row || []).forEach((val, c) => {
      if (val === null || val === undefined || val === "") return;
      let cell;
      if (typeof val === "object" && val && typeof val.f === "string") cell = fmlCell(val.f, { bl: !!val.bl, fa: val.fa, v: val.v });
      else if (typeof val === "number" && Number.isFinite(val)) cell = numCell(val, { bl: headerRow && r === 0 });
      else cell = txtCell(val, { bl: headerRow && r === 0, bg: headerRow && r === 0 ? "#f1f5f9" : undefined });
      celldata.push({ r, c, v: cell });
    });
  });
  return withCalcChain({ name: name || "Sheet1", celldata, row: Math.max(60, (rows || []).length + 20), column: Math.max(26, Math.max(0, ...(rows || []).map((r) => (r || []).length)) + 6) });
}

/** FortuneSheet sheet (with `data` matrix or `celldata`) -> 2-D array of JS values. */
function sheetToMatrix(sheet) {
  const out = [];
  const put = (r, c, cell) => {
    if (!cell) return;
    let v = cell.v;
    if (v === null || v === undefined) v = cell.m ?? null;
    if ((v === null || v === undefined || v === "") && !cell.f) return;
    if (v === "" || v === undefined) v = null;
    if (typeof v === "string" && cell.ct?.t === "n" && v.trim() !== "" && Number.isFinite(Number(v))) v = Number(v);
    while (out.length <= r) out.push([]);
    out[r][c] = { v, f: cell.f || null, t: cell.ct?.t };
  };
  if (Array.isArray(sheet?.data)) {
    sheet.data.forEach((row, r) => Array.isArray(row) && row.forEach((cell, c) => put(r, c, cell)));
  } else if (Array.isArray(sheet?.celldata)) {
    sheet.celldata.forEach((cd) => put(cd.r, cd.c, cd.v));
  }
  return out;
}

function sheetToWorksheet(sheet) {
  const matrix = sheetToMatrix(sheet);
  const aoa = matrix.map((row) => (row || []).map((cell) => (cell ? cell.v : null)));
  const ws = XLSX.utils.aoa_to_sheet(aoa.length ? aoa : [[]]);
  let maxR = 0, maxC = 0;
  matrix.forEach((row, r) => (row || []).forEach((cell, c) => {
    if (!cell) return;
    maxR = Math.max(maxR, r); maxC = Math.max(maxC, c);
    if (!cell.f) return;
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) ws[addr] = { t: "n", v: cell.v == null ? 0 : cell.v };
    ws[addr].f = String(cell.f).replace(/^=/, "");
  }));
  if (matrix.length) ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
  return ws;
}

/** SheetJS worksheet -> FortuneSheet sheet. */
function worksheetToSheet(ws, name) {
  const celldata = [];
  const ref = ws["!ref"];
  if (ref) {
    const range = XLSX.utils.decode_range(ref);
    for (let r = range.s.r; r <= range.e.r; r += 1) {
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;
        let v;
        if (cell.f) v = { ...fmlCell(cell.f, { v: cell.v ?? null }), m: cell.w ?? (cell.v == null ? "" : String(cell.v)) };
        else if (cell.t === "n") v = numCell(cell.v);
        else if (cell.t === "b") v = txtCell(cell.v ? "TRUE" : "FALSE");
        else if (cell.t === "d") v = txtCell(cell.w || (cell.v instanceof Date ? cell.v.toISOString().slice(0, 10) : String(cell.v)));
        else v = txtCell(cell.w ?? cell.v ?? "");
        celldata.push({ r, c, v });
      }
    }
  }
  return withCalcChain({ name: name || "Sheet1", celldata, row: Math.max(60, (celldata.length ? Math.max(...celldata.map((x) => x.r)) : 0) + 20), column: Math.max(26, (celldata.length ? Math.max(...celldata.map((x) => x.c)) : 0) + 6) });
}

/** Strip runtime-only fields and expand `data` into `celldata` for saving. */
function sheetsForSave(sheets) {
  return (sheets || []).map((sh, idx) => {
    const { data, luckysheet_select_save, luckysheet_selection_range, load, ...rest } = sh || {};
    let celldata = Array.isArray(rest.celldata) ? rest.celldata : null;
    if (Array.isArray(data)) {
      celldata = [];
      data.forEach((row, r) => Array.isArray(row) && row.forEach((cell, c) => { if (cell) celldata.push({ r, c, v: cell }); }));
    }
    return { ...rest, name: rest.name || `Sheet${idx + 1}`, celldata: celldata || [], order: idx, status: idx === 0 ? 1 : 0 };
  });
}

const safeFile = (s) => String(s || "spreadsheet").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "_") || "spreadsheet";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch (_) { return String(iso); }
};

// ───────────────────────────────────────────────────────────────────────────
// Restaurant starter templates
// ───────────────────────────────────────────────────────────────────────────

function templateSheets(kind, businessName) {
  const today = new Date().toISOString().slice(0, 10);
  if (kind === "cashup") {
    const rows = [
      ["Daily cash-up", businessName || "", "", today],
      [],
      ["Line", "Expected (R)", "Counted (R)", "Difference (R)", "Notes"],
      ["Cash sales", 0, 0, { f: "=C4-B4" }, ""],
      ["Card sales", 0, 0, { f: "=C5-B5" }, ""],
      ["Tips (cash)", 0, 0, { f: "=C6-B6" }, ""],
      ["Float", 0, 0, { f: "=C7-B7" }, ""],
      ["Payouts", 0, 0, { f: "=C8-B8" }, ""],
      ["TOTAL", { f: "=SUM(B4:B8)", bl: true }, { f: "=SUM(C4:C8)", bl: true }, { f: "=SUM(D4:D8)", bl: true }, ""],
    ];
    return [buildSheetFromRows("Cash-up", rows, { headerRow: false })];
  }
  if (kind === "stock") {
    const rows = [["Item", "Unit", "Opening", "Purchased", "Closing", "Used", "Cost / unit (R)", "Usage cost (R)"]];
    for (let i = 0; i < 12; i += 1) { const r = i + 2; rows.push(["", "", 0, 0, 0, { f: `=C${r}+D${r}-E${r}` }, 0, { f: `=F${r}*G${r}` }]); }
    rows.push(["TOTAL", "", "", "", "", "", "", { f: "=SUM(H2:H13)", bl: true }]);
    return [buildSheetFromRows("Stock count", rows)];
  }
  if (kind === "roster") {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const rows = [["Staff member", "Role", ...days, "Hours", "Rate (R/h)", "Pay (R)"]];
    for (let i = 0; i < 10; i += 1) { const r = i + 2; rows.push(["", "", 0, 0, 0, 0, 0, 0, 0, { f: `=SUM(C${r}:I${r})` }, 0, { f: `=J${r}*K${r}` }]); }
    rows.push(["TOTAL", "", "", "", "", "", "", "", "", { f: "=SUM(J2:J11)", bl: true }, "", { f: "=SUM(L2:L11)", bl: true }]);
    return [buildSheetFromRows("Roster", rows)];
  }
  return [{ name: "Sheet1", celldata: [], row: 84, column: 26 }];
}

const TEMPLATES = [
  { id: "blank", label: "Blank workbook", hint: "Start from an empty grid" },
  { id: "cashup", label: "Daily cash-up", hint: "Expected vs counted, with differences" },
  { id: "stock", label: "Stock count", hint: "Opening / purchased / closing usage" },
  { id: "roster", label: "Staff roster", hint: "Hours per day, rate and pay" },
];

// ───────────────────────────────────────────────────────────────────────────
// Workspace
// ───────────────────────────────────────────────────────────────────────────

export function SpreadsheetsWorkspace({ http, authHeaders, businessName, initialOpenId = null, onConsumedInitialOpen }) {
  const [docs, setDocs] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [current, setCurrent] = React.useState(null); // full doc being edited
  const [opening, setOpening] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [newDialog, setNewDialog] = React.useState({ open: false, name: "", template: "blank", periodDate: "" });
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState("");
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [versions, setVersions] = React.useState(null);
  const [preview, setPreview] = React.useState(null); // { version, sheets, saved_at }
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [leaveConfirm, setLeaveConfirm] = React.useState(false);
  const workbookRef = React.useRef(null);
  const latestSheetsRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [gridKey, setGridKey] = React.useState(0);
  // FortuneSheet emits onChange while it mounts/normalises the data; only
  // count changes after the grid has settled so a fresh open isn't "dirty".
  const gridReadyAtRef = React.useRef(0);
  React.useEffect(() => {
    gridReadyAtRef.current = Date.now() + 1200;
    // Evaluate every formula once the grid has mounted — imported files and
    // report exports may carry formulas without cached values.
    const t = setTimeout(() => {
      try {
        const sheets = workbookRef.current?.getAllSheets?.() || [];
        sheets.forEach((sh) => { try { workbookRef.current?.calculateFormula?.(sh.id); } catch (_) { /* noop */ } });
      } catch (_) { /* noop */ }
    }, 400);
    return () => clearTimeout(t);
  }, [gridKey, current?.id]);

  const loadDocs = React.useCallback(async () => {
    try {
      setLoadError(null);
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await http.get("/owner/spreadsheets", { headers: authHeaders, params });
      setDocs(r.data?.spreadsheets || []);
      setLoaded(true);
    } catch (e) {
      setLoadError(apiErrorMessage(e, "Could not load spreadsheets."));
      setLoaded(true);
    }
  }, [http, authHeaders, query, dateFrom, dateTo]);

  React.useEffect(() => { loadDocs(); }, [loadDocs]);

  const openDoc = React.useCallback(async (id) => {
    setOpening(true);
    try {
      const r = await http.get(`/owner/spreadsheets/${id}`, { headers: authHeaders });
      latestSheetsRef.current = null;
      setCurrent(r.data);
      setDirty(false);
      setGridKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open spreadsheet.");
    } finally {
      setOpening(false);
    }
  }, [http, authHeaders]);

  // Deep-link from "Send report to spreadsheet".
  React.useEffect(() => {
    if (initialOpenId) {
      openDoc(initialOpenId);
      if (typeof onConsumedInitialOpen === "function") onConsumedInitialOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenId]);

  // Warn before closing the tab with unsaved edits.
  React.useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const collectSheets = () => {
    try {
      const fromApi = workbookRef.current?.getAllSheets?.();
      if (Array.isArray(fromApi) && fromApi.length) return sheetsForSave(fromApi);
    } catch (_) { /* fall back */ }
    if (latestSheetsRef.current) return sheetsForSave(latestSheetsRef.current);
    return sheetsForSave(current?.sheets || []);
  };

  const save = async ({ note } = {}) => {
    if (!current) return false;
    setSaving(true);
    try {
      const sheets = collectSheets();
      const r = await http.put(`/owner/spreadsheets/${current.id}`, { sheets, note: note || undefined }, { headers: authHeaders });
      setCurrent((c) => ({ ...(c || {}), ...r.data }));
      setDirty(false);
      setDocs((list) => list.map((d) => (d.id === r.data.id ? { ...d, ...r.data, sheets: undefined } : d)));
      toast.success(`Saved · v${r.data.version}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save spreadsheet.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Autosave every 45 s while dirty.
  React.useEffect(() => {
    if (!current || !dirty) return undefined;
    const t = setTimeout(() => { save({ note: "Autosave" }); }, 45000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, current?.id]);

  const createDoc = async ({ name, sheets, source, periodDate }) => {
    try {
      const r = await http.post("/owner/spreadsheets", {
        name, sheets, source, period_date: periodDate || null,
      }, { headers: authHeaders });
      setDocs((list) => [{ ...r.data, sheets: undefined }, ...list]);
      latestSheetsRef.current = null;
      setCurrent(r.data);
      setDirty(false);
      setGridKey((k) => k + 1);
      toast.success(`"${r.data.name}" created`);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create spreadsheet.");
      return null;
    }
  };

  const duplicateDoc = async (id) => {
    try {
      const r = await http.post(`/owner/spreadsheets/${id}/duplicate`, {}, { headers: authHeaders });
      setDocs((list) => [{ ...r.data, sheets: undefined }, ...list]);
      toast.success("Copy created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not duplicate."); }
  };

  const deleteDoc = async (doc) => {
    try {
      await http.delete(`/owner/spreadsheets/${doc.id}`, { headers: authHeaders });
      setDocs((list) => list.filter((d) => d.id !== doc.id));
      if (current?.id === doc.id) { setCurrent(null); setDirty(false); }
      toast.success(`"${doc.name}" deleted`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
    setDeleteTarget(null);
  };

  const rename = async () => {
    const name = renameValue.trim();
    if (!name || !current) { setRenameOpen(false); return; }
    try {
      const r = await http.put(`/owner/spreadsheets/${current.id}`, { name }, { headers: authHeaders });
      setCurrent((c) => ({ ...c, name: r.data.name, version: r.data.version }));
      setDocs((list) => list.map((d) => (d.id === current.id ? { ...d, name: r.data.name } : d)));
      setRenameOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename."); }
  };

  const loadVersions = async () => {
    if (!current) return;
    setVersions(null);
    try {
      const r = await http.get(`/owner/spreadsheets/${current.id}/versions`, { headers: authHeaders });
      setVersions(r.data?.versions || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load history."); setVersions([]); }
  };

  const previewVersion = async (version) => {
    try {
      const r = await http.get(`/owner/spreadsheets/${current.id}/versions/${version}`, { headers: authHeaders });
      setPreview(r.data);
      setHistoryOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open that version."); }
  };

  const restoreVersion = async (version) => {
    try {
      const r = await http.post(`/owner/spreadsheets/${current.id}/versions/${version}/restore`, {}, { headers: authHeaders });
      latestSheetsRef.current = null;
      setCurrent(r.data);
      setDirty(false);
      setPreview(null);
      setHistoryOpen(false);
      setGridKey((k) => k + 1);
      toast.success(`Restored v${version} as v${r.data.version}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore."); }
  };

  // ── Export / import / print ───────────────────────────────────────────
  const activeSheet = () => {
    const sheets = collectSheets();
    let idx = 0;
    try {
      const live = workbookRef.current?.getAllSheets?.() || [];
      const active = live.findIndex((s) => s.status === 1);
      if (active >= 0) idx = active;
    } catch (_) { /* noop */ }
    return { sheets, idx: Math.min(idx, Math.max(0, sheets.length - 1)) };
  };

  const exportXlsx = () => {
    try {
      const { sheets } = activeSheet();
      const wb = XLSX.utils.book_new();
      sheets.forEach((sh, i) => XLSX.utils.book_append_sheet(wb, sheetToWorksheet(sh), String(sh.name || `Sheet${i + 1}`).slice(0, 31)));
      XLSX.writeFile(wb, `${safeFile(current?.name)}.xlsx`);
    } catch (e) { toast.error("Export failed."); }
  };

  const exportCsv = () => {
    try {
      const { sheets, idx } = activeSheet();
      const ws = sheetToWorksheet(sheets[idx]);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${safeFile(current?.name)}_${safeFile(sheets[idx]?.name)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { toast.error("Export failed."); }
  };

  const printSheet = () => {
    try {
      const { sheets, idx } = activeSheet();
      const ws = sheetToWorksheet(sheets[idx]);
      const table = XLSX.utils.sheet_to_html(ws, { header: "", footer: "" });
      const w = window.open("", "_blank", "width=1000,height=800");
      if (!w) { toast.error("Pop-up blocked — allow pop-ups to print."); return; }
      w.document.write(`<!doctype html><html><head><title>${current?.name || "Spreadsheet"}</title>
        <style>
          body{font-family:Inter,Arial,sans-serif;padding:24px;color:#0f172a}
          h1{font-size:18px;margin:0 0 4px} p{margin:0 0 16px;color:#64748b;font-size:12px}
          table{border-collapse:collapse;width:100%} td{border:1px solid #cbd5e1;padding:4px 8px;font-size:12px;white-space:nowrap}
          tr:first-child td{background:#f1f5f9;font-weight:600}
          @media print{ body{padding:0} }
        </style></head><body>
        <h1>${current?.name || "Spreadsheet"} — ${sheets[idx]?.name || "Sheet"}</h1>
        <p>${businessName || ""} · printed ${new Date().toLocaleString()}</p>${table}
        <script>window.onload=function(){window.print();}</script></body></html>`);
      w.document.close();
    } catch (e) { toast.error("Print failed."); }
  };

  const importFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellFormula: true, cellDates: true });
      const sheets = wb.SheetNames.map((n) => worksheetToSheet(wb.Sheets[n], n));
      if (!sheets.length) { toast.error("No sheets found in that file."); return; }
      const name = file.name.replace(/\.(xlsx|xlsm|xls|csv)$/i, "");
      await createDoc({ name, sheets, source: "import" });
    } catch (e) {
      toast.error("Could not read that file. Use .xlsx or .csv");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const goBack = () => {
    if (dirty) { setLeaveConfirm(true); return; }
    setCurrent(null);
  };

  // ── Rendering ─────────────────────────────────────────────────────────
  if (current) {
    const shown = preview ? preview.sheets : current.sheets;
    return (
      <div className="space-y-3" data-testid="spreadsheet-editor">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={goBack} data-testid="spreadsheet-back-button">
              <ArrowLeft className="mr-1 h-4 w-4" /> All spreadsheets
            </Button>
            <button
              type="button"
              className="min-w-0 truncate rounded-lg px-2 py-1 text-left text-base font-semibold hover:bg-muted"
              onClick={() => { setRenameValue(current.name); setRenameOpen(true); }}
              title="Rename"
              data-testid="spreadsheet-title"
            >
              {current.name}
            </button>
            <Badge variant="outline" className="rounded-full" data-testid="spreadsheet-version-badge">v{current.version}</Badge>
            {preview ? (
              <Badge className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300" data-testid="spreadsheet-preview-badge">
                Viewing v{preview.version} · {fmtDateTime(preview.saved_at)}
              </Badge>
            ) : dirty ? (
              <Badge className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300" data-testid="spreadsheet-dirty-badge">Unsaved changes</Badge>
            ) : (
              <span className="text-xs text-muted-foreground">Saved {fmtDateTime(current.updated_at)}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {preview ? (
              <>
                <Button size="sm" className="rounded-full" onClick={() => restoreVersion(preview.version)} data-testid="spreadsheet-restore-button">
                  <RotateCcw className="mr-1 h-4 w-4" /> Restore this version
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setPreview(null); setGridKey((k) => k + 1); }} data-testid="spreadsheet-exit-preview">
                  <X className="mr-1 h-4 w-4" /> Back to current
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" className="rounded-full" onClick={() => save()} disabled={saving} data-testid="spreadsheet-save-button">
                  <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setHistoryOpen(true); loadVersions(); }} data-testid="spreadsheet-history-button">
                  <History className="mr-1 h-4 w-4" /> History
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="spreadsheet-export-menu">
                      <Download className="mr-1 h-4 w-4" /> Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportXlsx} data-testid="spreadsheet-export-xlsx">Excel workbook (.xlsx)</DropdownMenuItem>
                    <DropdownMenuItem onClick={exportCsv} data-testid="spreadsheet-export-csv">Current sheet as CSV</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={printSheet} data-testid="spreadsheet-print"><Printer className="mr-2 h-4 w-4" /> Print current sheet</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        <div
          className="overflow-hidden rounded-2xl border border-border bg-white text-slate-900"
          style={{ height: "min(72vh, 780px)", minHeight: 420 }}
          data-testid="spreadsheet-grid"
        >
          <Workbook
            key={`${current.id}-${preview ? `p${preview.version}` : "live"}-${gridKey}`}
            ref={workbookRef}
            data={(shown && shown.length) ? shown.map((sh) => (Array.isArray(sh.calcChain) && sh.calcChain.length ? sh : withCalcChain(sh))) : [{ name: "Sheet1", celldata: [] }]}
            lang="en"
            allowEdit={!preview}
            showToolbar
            showFormulaBar
            showSheetTabs
            onChange={(sheets) => {
              if (preview) return;
              latestSheetsRef.current = sheets;
              if (Date.now() < gridReadyAtRef.current) return;
              setDirty(true);
            }}
          />
        </div>

        {/* Rename */}
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="rounded-2xl sm:max-w-md">
            <DialogHeader><DialogTitle>Rename spreadsheet</DialogTitle></DialogHeader>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") rename(); }} className="h-11 rounded-xl" data-testid="spreadsheet-rename-input" />
            <DialogFooter>
              <Button variant="outline" className="rounded-xl" onClick={() => setRenameOpen(false)}>Cancel</Button>
              <Button className="rounded-xl" onClick={rename} data-testid="spreadsheet-rename-save">Save name</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History */}
        <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
          <SheetContent className="w-full sm:max-w-md" data-testid="spreadsheet-history-panel">
            <SheetHeader>
              <SheetTitle>Version history</SheetTitle>
              <SheetDescription>Every save is kept. Open any version to view it exactly as it was, or restore it.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-2">
              {versions === null ? (
                <><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></>
              ) : versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No history yet.</p>
              ) : versions.map((v) => (
                <div key={v.version} className="flex items-center justify-between gap-2 rounded-xl border border-border p-3" data-testid={`spreadsheet-version-${v.version}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">v{v.version} {v.version === current.version ? <span className="text-xs font-normal text-muted-foreground">(current)</span> : null}</p>
                    <p className="truncate text-xs text-muted-foreground">{fmtDateTime(v.saved_at)} · {v.saved_by || "—"}{v.note ? ` · ${v.note}` : ""}</p>
                  </div>
                  {v.version !== current.version ? (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => previewVersion(v.version)} data-testid={`spreadsheet-version-open-${v.version}`}>Open</Button>
                  ) : null}
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Leave with unsaved changes */}
        <AlertDialog open={leaveConfirm} onOpenChange={setLeaveConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save before leaving?</AlertDialogTitle>
              <AlertDialogDescription>You have unsaved changes in "{current.name}".</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <Button variant="outline" className="rounded-xl" onClick={() => { setLeaveConfirm(false); setDirty(false); setCurrent(null); }} data-testid="spreadsheet-discard-button">Discard</Button>
              <AlertDialogAction onClick={async () => { const ok = await save(); setLeaveConfirm(false); if (ok) setCurrent(null); }} data-testid="spreadsheet-save-and-leave">Save & leave</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ── Document list ─────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="spreadsheets-list">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search spreadsheets…" className="h-10 rounded-full pl-9" data-testid="spreadsheets-search-input" />
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 w-auto rounded-full px-3 text-xs" data-testid="spreadsheets-date-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 w-auto rounded-full px-3 text-xs" data-testid="spreadsheets-date-to" />
            {(dateFrom || dateTo || query) ? (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setDateFrom(""); setDateTo(""); setQuery(""); }} data-testid="spreadsheets-clear-filters">Clear</Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="spreadsheets-import-input" />
          <Button variant="outline" className="rounded-full" onClick={() => fileInputRef.current?.click()} data-testid="spreadsheets-import-button">
            <FileUp className="mr-1 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" onClick={loadDocs} title="Refresh" data-testid="spreadsheets-refresh-button"><RefreshCw className="h-4 w-4" /></Button>
          <Button className="rounded-full" onClick={() => setNewDialog({ open: true, name: "", template: "blank", periodDate: "" })} data-testid="spreadsheets-new-button">
            <Plus className="mr-1 h-4 w-4" /> New spreadsheet
          </Button>
        </div>
      </div>

      {!loaded ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}</div>
      ) : loadError ? (
        <Card className="rounded-2xl border-destructive/30"><CardContent className="flex items-center justify-between p-5">
          <p className="text-sm text-destructive" data-testid="spreadsheets-error">{loadError}</p>
          <Button variant="outline" className="rounded-full" onClick={loadDocs}>Retry</Button>
        </CardContent></Card>
      ) : docs.length === 0 ? (
        <Card className="rounded-[24px] border-dashed"><CardContent className="flex flex-col items-center gap-3 p-10 text-center">
          <FileSpreadsheet className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="font-semibold" data-testid="spreadsheets-empty">{query || dateFrom || dateTo ? "No spreadsheets match those filters" : "No spreadsheets yet"}</p>
            <p className="text-sm text-muted-foreground">Create a blank workbook, start from a restaurant template, or import an Excel/CSV file.</p>
          </div>
          <Button className="rounded-full" onClick={() => setNewDialog({ open: true, name: "", template: "blank", periodDate: "" })}><Plus className="mr-1 h-4 w-4" /> New spreadsheet</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {docs.map((d) => (
            <Card key={d.id} className="group min-w-0 overflow-hidden rounded-2xl border-border transition-colors hover:border-foreground/30" data-testid={`spreadsheet-card-${d.id}`}>
              <CardContent className="p-4">
                <button type="button" className="block w-full min-w-0 max-w-full overflow-hidden text-left" onClick={() => openDoc(d.id)} disabled={opening} data-testid={`spreadsheet-open-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-emerald-500/10 p-2 text-emerald-700 dark:text-emerald-300"><FileSpreadsheet className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{d.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {d.period_date ? `For ${d.period_date} · ` : ""}Saved {fmtDateTime(d.updated_at)}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">v{d.version} · {d.sheet_count} sheet{d.sheet_count === 1 ? "" : "s"} · {d.cell_count} cells{d.updated_by ? ` · ${d.updated_by}` : ""}</p>
                    </div>
                  </div>
                </button>
                <div className="mt-3 flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => duplicateDoc(d.id)} data-testid={`spreadsheet-duplicate-${d.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Duplicate</Button>
                  <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteTarget(d)} data-testid={`spreadsheet-delete-${d.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New spreadsheet */}
      <Dialog open={newDialog.open} onOpenChange={(open) => setNewDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="new-spreadsheet-dialog">
          <DialogHeader>
            <DialogTitle>New spreadsheet</DialogTitle>
            <DialogDescription>Give it a name and pick a starting point.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-sheet-name">Name</Label>
              <Input id="new-sheet-name" value={newDialog.name} onChange={(e) => setNewDialog((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Cash-up 12 Sep" className="h-11 rounded-xl" data-testid="new-spreadsheet-name" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-sheet-date">For date <span className="text-muted-foreground">(optional — helps you find it later)</span></Label>
              <Input id="new-sheet-date" type="date" value={newDialog.periodDate} onChange={(e) => setNewDialog((d) => ({ ...d, periodDate: e.target.value }))} className="h-11 w-auto rounded-xl" data-testid="new-spreadsheet-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Start from</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setNewDialog((d) => ({ ...d, template: t.id }))}
                    className={`rounded-xl border p-3 text-left transition-colors ${newDialog.template === t.id ? "border-foreground bg-muted" : "border-border hover:bg-muted/60"}`}
                    data-testid={`new-spreadsheet-template-${t.id}`}
                  >
                    <p className="text-sm font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.hint}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setNewDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button
              className="rounded-xl"
              disabled={!newDialog.name.trim()}
              onClick={async () => {
                const doc = await createDoc({ name: newDialog.name.trim(), sheets: templateSheets(newDialog.template, businessName), source: newDialog.template, periodDate: newDialog.periodDate });
                if (doc) setNewDialog({ open: false, name: "", template: "blank", periodDate: "" });
              }}
              data-testid="new-spreadsheet-create"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes the spreadsheet and its entire version history. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90" onClick={() => deleteDoc(deleteTarget)} data-testid="spreadsheet-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SpreadsheetsWorkspace;
